import {
  AppWindow,
  ArrowUpRight,
  Beaker,
  BookOpen,
  CheckCircle2,
  Cloud,
  Coins,
  ExternalLink,
  HeartHandshake,
  Languages,
  Leaf,
  Lock,
  NotebookText,
  ShieldCheck,
  Sparkles
} from "lucide-react";
import type { ComponentType } from "react";
import { defaultLocale, localeCodes, localeMap, siteConfig, type Locale } from "./site/config";
import { content, type LocaleContent } from "./site/content";
import { localizedPath, matchRoute, type PageId, type RouteMatch } from "./site/routes";

type AppProps = {
  path?: string;
};

type IconComponent = ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

export function App({ path = "/" }: AppProps) {
  const route = matchRoute(path);
  const t = content[route.locale];

  return (
    <div className="site-shell">
      <Header route={route} t={t} />
      <main>
        {route.pageId === "home" && <HomePage locale={route.locale} t={t} />}
        {route.pageId === "dreamjar" && <DreamJarPage locale={route.locale} t={t} />}
        {route.pageId === "dreamjarPrivacy" && <LegalPage locale={route.locale} t={t} kind="privacy" />}
        {route.pageId === "dreamjarTerms" && <LegalPage locale={route.locale} t={t} kind="terms" />}
        {route.pageId === "notFound" && <NotFoundPage locale={route.locale} t={t} />}
      </main>
      <Footer locale={route.locale} t={t} />
    </div>
  );
}

function Header({ route, t }: { route: RouteMatch; t: LocaleContent }) {
  const switcherPage = switchablePage(route.pageId);

  return (
    <header className="site-header">
      <a className="brand-lockup" href={localizedPath("home", route.locale)} aria-label="MuzoLab">
        <span className="brand-mark" aria-hidden="true">
          <Beaker className="brand-mark-icon" aria-hidden />
        </span>
        <span>MuzoLab</span>
      </a>
      <nav className="main-nav" aria-label="Primary">
        <a href={localizedPath("dreamjar", route.locale)}>{t.nav.projects}</a>
        <a href={siteConfig.xUrl} target="_blank" rel="noreferrer">
          {t.nav.contact}
        </a>
      </nav>
      <label className="language-switcher">
        <Languages className="language-icon" aria-hidden />
        <span className="sr-only">{t.nav.language}</span>
        <select
          aria-label={t.nav.language}
          value={route.locale}
          onChange={(event) => {
            const nextLocale = event.currentTarget.value as Locale;
            window.location.href = localizedPath(switcherPage, nextLocale);
          }}
        >
          {localeCodes.map((locale) => (
            <option key={locale} value={locale}>
              {localeMap[locale].nativeLabel}
            </option>
          ))}
        </select>
      </label>
    </header>
  );
}

function HomePage({ locale, t }: { locale: Locale; t: LocaleContent }) {
  return (
    <>
      <section className="hero home-hero" aria-labelledby="home-title">
        <div className="hero-media" aria-hidden="true">
          <img className="hero-dreamjar-icon" src="/assets/dreamjar/app-icon.png" alt="" />
          <div className="hero-plate hero-plate-a" />
          <div className="hero-plate hero-plate-b" />
        </div>
        <div className="hero-inner">
          <p className="eyebrow">{t.home.eyebrow}</p>
          <h1 id="home-title">{t.home.headline}</h1>
          <p className="hero-lead">{t.home.lead}</p>
          <div className="hero-actions" aria-label="Primary actions">
            <a className="button primary" href={localizedPath("dreamjar", locale)}>
              {t.nav.projects}
              <ArrowUpRight aria-hidden />
            </a>
            <a className="button secondary" href={siteConfig.xUrl} target="_blank" rel="noreferrer">
              {t.nav.contact}
              <ExternalLink aria-hidden />
            </a>
          </div>
        </div>
      </section>

      <section className="section" id="projects" aria-labelledby="projects-title">
        <div className="section-heading">
          <p className="eyebrow">{siteConfig.siteName}</p>
          <h2 id="projects-title">{t.home.projectSectionTitle}</h2>
          <p>{t.home.projectSectionLead}</p>
        </div>
        <div className="project-grid">
          <a className="project-card" href={localizedPath("dreamjar", locale)}>
            <span className="project-status">{t.nav.comingSoon}</span>
            <img src="/assets/dreamjar/app-icon.png" alt="DreamJar" />
            <span className="project-label">{t.home.dreamJarCard.label}</span>
            <strong>{t.home.dreamJarCard.title}</strong>
            <span>{t.home.dreamJarCard.text}</span>
            <span className="inline-link">
              {t.nav.learnMore}
              <ArrowUpRight aria-hidden />
            </span>
          </a>
        </div>
      </section>

      <section className="section muted-band" aria-labelledby="principles-title">
        <div className="section-heading">
          <p className="eyebrow">MuzoLab</p>
          <h2 id="principles-title">{t.home.principlesTitle}</h2>
          <p>{t.home.principlesLead}</p>
        </div>
        <div className="principle-grid">
          {t.home.principles.map((principle, index) => {
            const icons: IconComponent[] = [Sparkles, ShieldCheck, Leaf];
            const Icon = icons[index] ?? Sparkles;
            return (
              <article className="principle-item" key={principle.title}>
                <Icon className="item-icon" aria-hidden />
                <h3>{principle.title}</h3>
                <p>{principle.text}</p>
              </article>
            );
          })}
        </div>
      </section>
    </>
  );
}

function DreamJarPage({ locale, t }: { locale: Locale; t: LocaleContent }) {
  return (
    <>
      <section className="hero product-hero dreamjar-hero" aria-labelledby="dreamjar-title">
        <div className="hero-media dreamjar-media" aria-hidden="true">
          <DreamJarPhone t={t} />
        </div>
        <div className="hero-inner">
          <p className="eyebrow">{t.dreamjar.eyebrow}</p>
          <h1 id="dreamjar-title">DreamJar</h1>
          <p className="hero-subtitle">{t.dreamjar.headline}</p>
          <p className="hero-lead">{t.dreamjar.lead}</p>
          <div className="badge-row" aria-label="Product facts">
            {t.dreamjar.proofPoints.map((point) => (
              <span className="badge" key={point}>
                <CheckCircle2 aria-hidden />
                {point}
              </span>
            ))}
          </div>
          <div className="hero-actions" aria-label="DreamJar actions">
            <a className="button primary" href={siteConfig.xUrl} target="_blank" rel="noreferrer">
              {t.nav.followOnX}
              <ExternalLink aria-hidden />
            </a>
            <span className="button disabled" aria-disabled="true">
              {t.dreamjar.appStoreSoon}
            </span>
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="dreamjar-features">
        <div className="section-heading">
          <p className="eyebrow">{t.dreamjar.status}</p>
          <h2 id="dreamjar-features">{t.dreamjar.featuresTitle}</h2>
          <p>{t.dreamjar.featuresLead}</p>
        </div>
        <div className="feature-grid">
          {t.dreamjar.features.map((feature, index) => {
            const icons: IconComponent[] = [Coins, NotebookText, HeartHandshake, Lock];
            const Icon = icons[index] ?? Sparkles;
            return (
              <article className="feature-card" key={feature.title}>
                <Icon className="item-icon" aria-hidden />
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="section split-band" aria-labelledby="privacy-title">
        <div className="section-copy">
          <p className="eyebrow">Local-first</p>
          <h2 id="privacy-title">{t.dreamjar.privacyTitle}</h2>
          <p>{t.dreamjar.privacyLead}</p>
          <div className="text-link-row">
            <a href={localizedPath("dreamjarPrivacy", locale)}>{t.nav.privacy}</a>
            <a href={localizedPath("dreamjarTerms", locale)}>{t.nav.terms}</a>
          </div>
        </div>
        <div className="signal-list" aria-label="DreamJar architecture">
          <Signal icon={AppWindow} title="Core Data" text="On-device app records" />
          <Signal icon={Cloud} title="iCloud" text="Private user sync" />
          <Signal icon={ShieldCheck} title="StoreKit 2" text="Apple-managed purchases" />
        </div>
      </section>

      <section className="section cta-band" aria-labelledby="dreamjar-cta">
        <p className="eyebrow">DreamJar</p>
        <h2 id="dreamjar-cta">{t.dreamjar.ctaTitle}</h2>
        <p>{t.dreamjar.ctaLead}</p>
        <a className="button primary" href={siteConfig.xUrl} target="_blank" rel="noreferrer">
          {t.nav.followOnX}
          <ExternalLink aria-hidden />
        </a>
      </section>
    </>
  );
}

function DreamJarPhone({ t }: { t: LocaleContent }) {
  return (
    <div className="phone-frame">
      <div className="phone-screen">
        <div className="phone-header">
          <img src="/assets/dreamjar/app-icon.png" alt="" />
          <div>
            <strong>{t.dreamjar.phone.child}</strong>
            <span>{t.dreamjar.phone.plan}</span>
          </div>
        </div>
        <div className="phone-stats">
          <span>
            <b>3</b>
            {t.dreamjar.phone.activeDreams}
          </span>
          <span>
            <b>145</b>
            {t.dreamjar.phone.coins}
          </span>
          <span>
            <b>8</b>
            {t.dreamjar.phone.journal}
          </span>
        </div>
        <div className="phone-jar">
          <img src="/assets/dreamjar/jar-cover.png" alt="" />
          <div>
            <strong>{t.dreamjar.phone.dreamName}</strong>
            <span>{t.dreamjar.phone.progress}</span>
            <div className="progress-bar">
              <span />
            </div>
          </div>
        </div>
        <div className="phone-note">
          <BookOpen aria-hidden />
          <span>{t.dreamjar.phone.note}</span>
        </div>
      </div>
    </div>
  );
}

function LegalPage({ locale, t, kind }: { locale: Locale; t: LocaleContent; kind: "privacy" | "terms" }) {
  const isPrivacy = kind === "privacy";
  const title = isPrivacy ? t.legal.privacyTitle : t.legal.termsTitle;
  const lead = isPrivacy ? t.legal.privacyLead : t.legal.termsLead;
  const sections = isPrivacy ? t.legal.privacySections : t.legal.termsSections;

  return (
    <article className="legal-page">
      <header className="legal-header">
        <a className="inline-link" href={localizedPath("dreamjar", locale)}>
          {t.nav.backHome}
        </a>
        <p className="eyebrow">DreamJar</p>
        <h1>{title}</h1>
        <p>{lead}</p>
        <span>{t.legal.updated}</span>
      </header>
      <div className="legal-content">
        {sections.map((section) => (
          <section key={section.title}>
            <h2>{section.title}</h2>
            {section.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </section>
        ))}
      </div>
    </article>
  );
}

function NotFoundPage({ locale, t }: { locale: Locale; t: LocaleContent }) {
  return (
    <section className="legal-page compact">
      <header className="legal-header">
        <h1>{t.notFound.title}</h1>
        <p>{t.notFound.text}</p>
        <a className="button primary" href={localizedPath("home", locale)}>
          {t.nav.backHome}
        </a>
      </header>
    </section>
  );
}

function Footer({ locale, t }: { locale: Locale; t: LocaleContent }) {
  return (
    <footer className="site-footer">
      <div>
        <strong>MuzoLab</strong>
        <span>{siteConfig.domain}</span>
      </div>
      <nav aria-label="Footer">
        <a href={localizedPath("dreamjarPrivacy", locale)}>{t.nav.privacy}</a>
        <a href={localizedPath("dreamjarTerms", locale)}>{t.nav.terms}</a>
        <a href={siteConfig.xUrl} target="_blank" rel="noreferrer">
          {siteConfig.xHandle}
        </a>
      </nav>
    </footer>
  );
}

function Signal({ icon: Icon, title, text }: { icon: IconComponent; title: string; text: string }) {
  return (
    <div className="signal-item">
      <Icon className="item-icon" aria-hidden />
      <div>
        <strong>{title}</strong>
        <span>{text}</span>
      </div>
    </div>
  );
}

function switchablePage(pageId: PageId): Exclude<PageId, "notFound"> {
  if (pageId === "notFound") {
    return "home";
  }
  return pageId;
}

export function pageTitleFor(route: RouteMatch): string {
  const t = content[route.locale];
  switch (route.pageId) {
    case "home":
      return t.home.metaTitle;
    case "dreamjar":
      return t.dreamjar.metaTitle;
    case "dreamjarPrivacy":
      return t.legal.privacyMetaTitle;
    case "dreamjarTerms":
      return t.legal.termsMetaTitle;
    default:
      return route.locale === defaultLocale ? "MuzoLab" : `${t.notFound.title} | MuzoLab`;
  }
}

export function pageDescriptionFor(route: RouteMatch): string {
  const t = content[route.locale];
  switch (route.pageId) {
    case "home":
      return t.home.metaDescription;
    case "dreamjar":
      return t.dreamjar.metaDescription;
    case "dreamjarPrivacy":
      return t.legal.privacyMetaDescription;
    case "dreamjarTerms":
      return t.legal.termsMetaDescription;
    default:
      return t.notFound.text;
  }
}
